// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { MessageDescriptor, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";
import { ActionType, IAction, raiseAction } from "actions";
import { connect } from "react-redux";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { IState } from "states/states";
import { isSet } from "shared/utils/utils";

import PeriodSelect from "./PeriodSelect";
import SearchFilter from "./SearchFilter";

import FilterBadge from "./FilterBadge";

import IconSearch from "icons/iconSearch";
import IconFilterExtend from "icons/iconFilterExtend";
import IconPeriodExtend from "icons/iconPeriodicExtend";

import "./SiteBar.scss";

export interface ISiteBarSearchFieldProps {
  placeholder?: MessageDescriptor;
  toolTip?: MessageDescriptor;
  dataListId?: string;
}

export interface ISiteBarExtProps {
  showBackButton?: boolean;
  showPeriodSelect: boolean;
  searchFieldProps?: ISiteBarSearchFieldProps;
}

export interface ISiteBarProps {
  filterText?: string;
}

interface ISiteBarState {
  showSearchFilter: boolean;
  showFiltersExpanded: boolean;
}

export const mapStateToProps = (state: IState) => {
  const props: ISiteBarProps = {
    filterText: state.gui.searchFilterText,
  };

  return props;
};

export interface ISiteBarDispatch {
  onSearchFilterTextChanged?: (filterText: string) => void;
}

export const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteBarDispatch = {
    onSearchFilterTextChanged: (filterText: string) => {
      dispatch(raiseAction(ActionType.ACT_SEARCH_FILTER_TEXT_CHANGED, filterText));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  searchPreamble: {
    id: "Search",
  },
  filterPreamble: {
    id: "Filter",
  },
  noCategories: {
    id: "Without mapping",
  },
});

export class SiteBarComponent<P extends object = object> extends React.Component<
  ISiteBarProps & ISiteBarExtProps & ISiteBarDispatch & RoutedProps & P,
  ISiteBarState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: ISiteBarState = {
    showSearchFilter: false,
    showFiltersExpanded: false,
  };

  public render() {
    const backButton = this.renderBackButton();
    const selectionButtons = this.renderSelectionButtons();
    const filterButtons = this.renderFilterButtons();
    const hasFilterControls =
      Boolean(backButton) || Boolean(selectionButtons) || Boolean(filterButtons);
    const hasSearch = isSet(this.props.searchFieldProps);
    // the filter-toggle (funnel icon) is only needed when there are real filter/selection
    // controls that would otherwise clutter the period selector's space
    const hasCollapsibleFilters = this.props.showPeriodSelect === true && hasFilterControls;
    // whenever there's a filter toggle OR a search field, the expanded content can still
    // collide with the period selector on narrow screens, so it needs to be swapped out
    const needsSpaceSwap = this.props.showPeriodSelect === true && (hasFilterControls || hasSearch);
    const isExpanded =
      hasCollapsibleFilters ? this.state.showFiltersExpanded : this.state.showSearchFilter;

    return (
      <div className="sitebar d-flex flex-column mb-2">
        <div className="d-flex w-100 align-items-center">
          <div
            className={`sh-options d-flex justify-content-start ${
              hasCollapsibleFilters ?
                this.state.showFiltersExpanded ?
                  "sh-options--expanded"
                : "sh-options--collapsed"
              : ""
            }`}
          >
            {hasCollapsibleFilters && (
              <Button
                className="btn-header d-sm-none filter-toggle"
                variant=""
                onClick={() =>
                  this.setState({ showFiltersExpanded: !this.state.showFiltersExpanded })
                }
                title={this.state.showFiltersExpanded ? "Hide filters" : "Show filters"}
              >
                <IconFilterExtend width={"24px"} height={"24px"} />
              </Button>
            )}
            <div className="filters-content">
              {backButton}
              {selectionButtons}
              {filterButtons}
              {this.renderSearch(!hasCollapsibleFilters && needsSpaceSwap)}
            </div>
          </div>
          <div className={"badges d-none d-lg-flex justify-content-start ms-2 me-0 mb-0 mt-0"}>
            {this.renderBadges()}
          </div>
          <div
            className={`sh-options headerbuttons d-flex justify-content-end me-0 mb-0 mt-0 ${isExpanded ? "headerbuttons--expanded" : "headerbuttons--collapsed"}`}
          >
            {this.props.showPeriodSelect === true ?
              <>
                <PeriodSelect className={`mt-0 mb-0 ${isExpanded ? "d-none" : ""} d-sm-flex`} />
                {needsSpaceSwap && (
                  <Button
                    className="btn-header d-sm-none mt-0 collapse-expand-button"
                    variant=""
                    onClick={() =>
                      hasCollapsibleFilters ?
                        this.setState({ showFiltersExpanded: false })
                      : this.setState({ showSearchFilter: false })
                    }
                    title="Show date selection"
                    style={{ display: isExpanded ? "flex" : "none" }}
                  >
                    <IconPeriodExtend width={"24px"} height={"24px"} />
                  </Button>
                )}
              </>
            : <></>}
          </div>
        </div>
      </div>
    );
  }

  // override in case a back button is needed
  public renderBackButton(): React.ReactNode {
    return null;
  }

  public renderSelectionButtons(): React.ReactNode {
    return null;
  }

  // override in case filter buttons are needed
  public renderFilterButtons(): React.ReactNode {
    return null;
  }

  private renderSearch(hideToggleWhenExpanded: boolean) {
    const searchFieldProps: ISiteBarSearchFieldProps | undefined = this.props.searchFieldProps;
    const filterTextBadgeVisible = isSet(this.props.filterText) && this.props.filterText !== "";
    const hideToggle = hideToggleWhenExpanded && this.state.showSearchFilter;
    const searchButton =
      isSet(this.props.searchFieldProps) ?
        <>
          <div
            className={`position-relative ms-2 ${hideToggle ? "d-none d-sm-inline-flex" : "d-inline-flex"}`}
          >
            <Button
              className={`btn-header${this.state.showSearchFilter ? " active" : ""}  ms-2`}
              variant=""
              onClick={() => {
                this.setState({ showSearchFilter: !this.state.showSearchFilter });
              }}
            >
              <IconSearch width={"20px"} height={"20px"} viewBox={"0 0 50 50"} />
            </Button>
            {filterTextBadgeVisible && (
              <span className="filter-active-indicator d-lg-none position-absolute top-0 start-100 translate-middle">
                !
              </span>
            )}
          </div>
          {isSet(searchFieldProps) && this.state.showSearchFilter === true && (
            <SearchFilter
              className="ms-2"
              filterText={this.props.filterText ?? ""}
              autoFocus={this.state.showSearchFilter}
              placeholder={
                isSet(searchFieldProps.placeholder) ?
                  this.context.formatMessage(searchFieldProps.placeholder!)
                : ""
              }
              tooltip={
                isSet(searchFieldProps.toolTip) ?
                  this.context.formatMessage(searchFieldProps.toolTip!)
                : ""
              }
              onChange={(filterText) => {
                this.props.onSearchFilterTextChanged?.(filterText);
              }}
              datalist={searchFieldProps.dataListId}
            />
          )}
        </>
      : <></>;

    return searchButton;
  }

  private renderBadges() {
    return (
      <>
        {this.renderSearchBadges()}
        {this.renderFilterBadges()}
      </>
    );
  }

  private renderSearchBadges() {
    const filterTextBadgeVisible = isSet(this.props.filterText) && this.props.filterText !== "";
    return (
      filterTextBadgeVisible && (
        <FilterBadge
          text={this.props.filterText ?? ""}
          type={this.context.formatMessage(messages.searchPreamble)}
          onClear={() => {
            this.props.onSearchFilterTextChanged?.("");
          }}
          onOpen={() => {
            this.setState({ showSearchFilter: true });
          }}
        />
      )
    );
  }

  // override in case filter badges are needed
  public renderFilterBadges() {
    return <></>;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteBarComponent));
